import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { KitItem } from 'src/types/perfectOrder';
import { AddedPerfectOrderItem } from './PerfectOrderItemPicker';

interface Props {
  kit: KitItem;
  kitQuantity?: number;
  existingItemNumbers: string[];
  highlightTerm?: string;
  disabled?: boolean;
  onAddItems: (items: AddedPerfectOrderItem[]) => void;
}

interface ComponentSelection {
  selected: boolean;
  quantity: number;
}

const PerfectOrderKitItemsButton: FC<Props> = ({ kit, kitQuantity = 1, existingItemNumbers, highlightTerm = '', disabled = false, onAddItems }) => {
  const [open, setOpen] = useState(false);
  const [selections, setSelections] = useState<Record<string, ComponentSelection>>({});
  const existingItemNumberSet = useMemo(() => new Set(existingItemNumbers.map((itemNumber) => itemNumber.toLowerCase())), [existingItemNumbers]);
  const normalizedHighlight = highlightTerm.trim().toLowerCase();
  const componentMatchesHighlight = (itemNumber: string, description?: string) =>
    normalizedHighlight.length > 0 && (itemNumber.toLowerCase().includes(normalizedHighlight) || (description?.toLowerCase().includes(normalizedHighlight) ?? false));

  useEffect(() => {
    if (!open) return;
    setSelections(
      Object.fromEntries(
        kit.components.map((component) => [
          component.itemNumber,
          {
            selected: false,
            quantity: 1,
          },
        ])
      )
    );
  }, [kit.components, open]);

  const getMaxQuantity = (quantityNeeded: number) => Math.max(1, Number(quantityNeeded || 0) * Math.max(1, Number(kitQuantity || 1)));

  const updateSelection = (itemNumber: string, values: Partial<ComponentSelection>) => {
    setSelections((previous) => ({
      ...previous,
      [itemNumber]: {
        selected: previous[itemNumber]?.selected ?? false,
        quantity: previous[itemNumber]?.quantity ?? 1,
        ...values,
      },
    }));
  };

  const handleQuantityChange = (itemNumber: string, maxQuantity: number) => (event: NumericTextBoxChangeEvent) => {
    const quantity = Math.min(maxQuantity, Math.max(1, event.value ?? 1));
    updateSelection(itemNumber, { quantity });
  };

  const selectedComponents = kit.components.filter((component) => selections[component.itemNumber]?.selected && !existingItemNumberSet.has(component.itemNumber.toLowerCase()));

  const handleAdd = () => {
    onAddItems(
      selectedComponents.map((component) => ({
        itemNumber: component.itemNumber,
        vendorNumber: component.vendorNumber,
        description: component.description,
        quantity: selections[component.itemNumber]?.quantity ?? 1,
        customerPrice: component.price,
        kitParentItemNumber: kit.kitItemNumber,
      }))
    );
    setOpen(false);
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<AccountTreeOutlinedIcon />} onClick={() => setOpen(true)} disabled={disabled}>
        Kit Items
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Damaged Kit Items for {kit.kitItemNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select only the damaged or shorted components and enter the quantity being requested.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={70}>Select</TableCell>
                <TableCell>Item #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell width={120}>Kit Qty</TableCell>
                <TableCell width={140}>Requested Qty</TableCell>
                <TableCell width={120}>Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {kit.components.map((component) => {
                const duplicate = existingItemNumberSet.has(component.itemNumber.toLowerCase());
                const selection = selections[component.itemNumber] ?? { selected: false, quantity: 1 };
                const maxQuantity = getMaxQuantity(component.quantityNeeded);
                const highlighted = componentMatchesHighlight(component.itemNumber, component.description);
                return (
                  <TableRow key={component.itemNumber} selected={highlighted}>
                    <TableCell>
                      <Checkbox
                        checked={selection.selected && !duplicate}
                        disabled={duplicate}
                        onChange={(event) => updateSelection(component.itemNumber, { selected: event.target.checked })}
                      />
                    </TableCell>
                    <TableCell>{component.itemNumber}</TableCell>
                    <TableCell>
                      <Box>{component.description}</Box>
                      {duplicate && (
                        <Typography variant="caption" color="text.secondary">
                          Already on this perfect order
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{maxQuantity}</TableCell>
                    <TableCell>
                      <NumericTextBox
                        value={selection.quantity}
                        min={1}
                        max={maxQuantity}
                        spinners
                        format="n0"
                        width={100}
                        disabled={!selection.selected || duplicate}
                        onChange={handleQuantityChange(component.itemNumber, maxQuantity)}
                      />
                    </TableCell>
                    <TableCell>{component.price.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 5 })}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={selectedComponents.length === 0}>
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PerfectOrderKitItemsButton;
